import { useAnalytics } from "@codat/analytics";
import {
  BellIcon,
  DataTable,
  ErrorDialog,
  PaginationRange,
  RightArrowIcon,
  SettingsIcon,
  ZeroState,
  useToast,
} from "@codat/orchard-ui";
import { ColumnDefinition } from "@codat/orchard-ui/dist/es/components/DataTable/types";
import { useGetAlertsQuery } from "api/public";
import classNames from "classnames";
import useLocalStorage from "hooks/useLocalStorage";
import { useUserState } from "portal-ui-remote/UserStateContext";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { formatDateAndTimeWithShortString } from "utils/formatDate";
import formatUrl from "utils/formatUrl";

import { paths } from "components/App/paths";
import Button from "components/Button";
import GenericCell from "components/GenericCell";
import PageHeader from "components/PageHeader";

import styles from "./WebhookEvents.module.scss";

type CtaCellProps = {
  key: "cta";
  value: {
    companyId: string;
    onClick: () => void;
    hoveredRowIndex: number | undefined;
    index: number;
  };
};

const CtaCell: React.FC<Pick<CtaCellProps, "value">> = ({ value }) => {
  const buttonVariant =
    value.hoveredRowIndex === value.index ? "primary" : "secondary";
  const buttonColor =
    value.hoveredRowIndex === value.index ? "action" : "interface";
  return (
    <Button
      label="Summary"
      size="small"
      variant={buttonVariant}
      color={buttonColor}
      buttonName="Summary"
      eventProperties={{ companyId: value.companyId }}
      data-testid="summary-button"
      onClick={(
        event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
      ) => {
        value.onClick();
        event.stopPropagation();
      }}
    >
      <RightArrowIcon />
    </Button>
  );
};

export const LegacyWebhookEvents = () => {
  const resultsPerPage = 20;
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | undefined>();
  const history = useHistory();
  const { sendEvent } = useAnalytics();
  const { addToast } = useToast();

  const { userState } = useUserState();

  const [latestAlertViewed, setLatestAlertViewed, getLatestAlertViewedManual] =
    useLocalStorage<string>(`${userState?.["id"]}-latestAlertViewed`, "", {
      isManual: true,
    });

  const [{ data, loading, error }] = useGetAlertsQuery({
    page: pageIndex,
    pageSize: resultsPerPage,
  });

  useEffect(() => {
    if (data?.results?.length) {
      const latest = data.results.reduce(
        (max, r) => (max > r.raisedOnUtc ? max : r.raisedOnUtc),
        data?.results[0].raisedOnUtc
      );

      latest > getLatestAlertViewedManual() && setLatestAlertViewed(latest);
    }
  }, [getLatestAlertViewedManual, setLatestAlertViewed, data]);

  const rowInfo = useMemo(() => {
    if (!data?.results) return [];

    const hasSomeNewAlertsOnPage =
      latestAlertViewed &&
      data.results.some((x) => latestAlertViewed < x.raisedOnUtc);

    return data.results.map((row, index) => {
      const onViewCompanyClick = () => {
        history.push(
          formatUrl(paths.companies.SUMMARY, {
            companyId: row.companyId,
          })
        );
      };

      const onCopyCompanyId = () => {
        addToast({
          message: "Copied company ID to clipboard",
          variant: "success",
        });
        sendEvent("Company ID - Copy", { companyId: row.companyId });
      };

      return {
        onMouseEnter: () => setHoveredRowIndex(index),
        onMouseLeave: () => setHoveredRowIndex(undefined),
        data: [
          {
            key: "event",
            value: {
              content:
                row.ruleType === "DataConnectionStatusChanged"
                  ? "Data connection status changed"
                  : row.ruleType.charAt(0) +
                    row.ruleType.slice(1).toLowerCase(),
              subtext: row.message,
              testId: "webhook-events-table-event",
              iconLeft: hasSomeNewAlertsOnPage ? (
                <div
                  data-testid="new-alert-icon"
                  className={classNames(styles.dot, {
                    [styles.filled]:
                      latestAlertViewed && latestAlertViewed < row.raisedOnUtc,
                  })}
                />
              ) : undefined,
            },
          },
          {
            key: "company",
            value: {
              content: row?.companyName || "-",
              subtext: row.companyId,
              onCopySubtext: onCopyCompanyId,
              clipboardCopyLabel: "Copy company Id",
              testId: "webhook-events-table-company",
              hoveredRowIndex: hoveredRowIndex,
              index,
            },
          },
          {
            key: "dateRaised",
            value: {
              content: formatDateAndTimeWithShortString(
                row.raisedOnUtc,
                true,
                true
              ),
              testId: "webhook-events-table-date-raised",
            },
          },
          {
            key: "cta",
            value: {
              companyId: row.companyId,
              hoveredRowIndex: hoveredRowIndex,
              index,
              onClick: onViewCompanyClick,
            },
          },
        ],
      };
    });
  }, [
    data?.results,
    hoveredRowIndex,
    history,
    addToast,
    sendEvent,
    latestAlertViewed,
  ]);

  const columnDefs: ColumnDefinition<
    "event" | "company" | "dateRaised" | "cta"
  >[] = [
    {
      key: "event",
      label: "Event",
      component: GenericCell,
      colWidth: 50,
    },
    {
      key: "company",
      label: "Company",
      component: GenericCell,
    },
    {
      key: "dateRaised",
      label: "Date raised",
      component: GenericCell,
    },
    {
      key: "cta",
      label: "",
      component: CtaCell,
      align: "right",
    },
  ];

  const onUpdatePageIndex = (page: number) => {
    setPageIndex(page);
    sendEvent("Webhook events - Pagination - Page Index - Change", {
      newPageIndex: page,
    });
  };

  const numberOfPages = data
    ? Math.ceil(data?.totalResults / resultsPerPage)
    : 0;
  const columns = loading ? [] : columnDefs;

  return (
    <>
      <PageHeader header="Webhook events">
        <Button
          buttonName="Manage Settings"
          to={paths.settings.RULES}
          color="interface"
          variant="secondary"
          aria-label="Manage settings"
          data-testid="webhook-settings-button"
        >
          <SettingsIcon className={styles.btnIcon} />
          Manage settings
        </Button>
      </PageHeader>
      {error && (
        <ErrorDialog
          statusCode={error.response?.data?.statusCode}
          message={error.response?.data?.error}
          correlationId={error.response?.data?.correlationId}
        />
      )}
      {!error && !loading && rowInfo.length < 1 && (
        <ZeroState
          className={styles.emptyState}
          title="No webhook events"
          description="Webhook events will appear here"
          icon={<BellIcon />}
        />
      )}
      {!error && (loading || (!loading && rowInfo.length >= 1)) && (
        <div>
          <DataTable
            isSkeleton={loading}
            skeletonRows={20}
            columnDefs={columns}
            data-testid="webhook-events-table"
            rowsInfo={rowInfo}
            isLayoutAuto
            hasBorder
            className={styles.dataTable}
          />
          <PaginationRange
            total={numberOfPages}
            onUpdatePageIndex={(page) => onUpdatePageIndex(page)}
            isPrevNext
          />
        </div>
      )}
    </>
  );
};
